/**
 * SetupInProgressBanner - setup is not finished yet.
 *
 * Admins who have done their own steps use the portal while LipaFlow
 * connects their bank account and the first payment arrives; the banner
 * links back to the "Get paid" step. Other staff are told an administrator
 * is finishing setup.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, OnboardingProgress, useAuthStore, usePermission, useVerticalConfig } from '@lipaflow/shared'
import { Link } from 'react-router-dom'
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { isDemoTenant, isInitialSetupStepComplete, isOwnSetupDone } from '../../lib/onboardingRouting'

export default function SetupInProgressBanner() {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)
  const { labels } = useVerticalConfig()
  const [dismissed, setDismissed] = useState(false)

  const isDemo = isDemoTenant(tenant)
  const isNonAdmin = !usePermission('users:manage')

  const { data: progress } = useQuery<OnboardingProgress>({
    queryKey: ['onboarding-progress-banner', user?.tenant_id],
    queryFn: async () => {
      const res = await api.get('/onboarding/progress')
      return res.data
    },
    enabled: Boolean(user?.tenant_id && !isDemo && !dismissed),
    staleTime: 60_000,
  })

  if (dismissed || isDemo) return null
  if (!progress || progress.is_complete || progress.is_admin_setup) return null
  if (isNonAdmin ? !isInitialSetupStepComplete(progress) : !isOwnSetupDone(progress)) return null

  const institutionLabel = labels.institution || 'Organization'

  return (
    <div
      role="status"
      aria-label="Setup in progress notice"
      className="bg-info-50 border-b border-info-200 text-info-900 px-4 py-2 flex items-center justify-between gap-3 animate-fade-in"
    >
      <div className="flex items-center gap-2 font-inter text-body-sm min-w-0">
        <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-info-600" />
        {isNonAdmin ? (
          <p className="truncate">
            <span className="font-semibold text-info-800">Initial Setup in Progress:</span> Your
            administrator is finalizing the {institutionLabel.toLowerCase()} configuration. Full
            features will activate once setup completes.
          </p>
        ) : (
          <p className="truncate">
            <span className="font-semibold text-info-800">Almost live:</span> payments and messages
            start once your collection account is connected and the first payment reaches it.{' '}
            <Link to="/activation" className="font-semibold underline hover:text-info-700">
              See what is left
            </Link>
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss banner"
        className="p-1 rounded hover:bg-info-100 transition-colors flex-shrink-0 text-info-600 hover:text-info-800"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  )
}
